// The word of the day: no room, no name. The browser keeps the attempts and the statistics;
// the server only colors each guess, so the word never reaches the page early.

import type { Lang } from "../src/languages";
import {
  BASE_PATH,
  DAILY_TIME_ZONE,
  MAX_ATTEMPTS,
  type DailyAnswerResponse,
  type DailyGuessResponse,
  type DailyToday,
  type Mark,
} from "../src/protocol";
import { track } from "./analytics";
import { contrastToggle, GuessGrid, Keyboard, shareSquares } from "./board";
import { h } from "./dom";
import { Draft } from "./draft";
import { brandMark, getWordLang, setWordLang, siteFooter, wordLangSelect } from "./home";
import { uiLangSelect } from "./langPicker";
import { STRINGS } from "./strings";

interface DayState {
  day: string;
  number: number;
  guesses: string[];
  marks: Mark[][];
  done: boolean;
  won: boolean;
  /** Only known once the day is over (solved, or six tries used). */
  answer: string | null;
}

interface Stats {
  played: number;
  won: number;
  streak: number;
  best: number;
  /** Last day counted, so a reload never counts a day twice. */
  lastDay: string | null;
  /** Wins by number of tries (index 0 = first try). */
  distribution: number[];
}

const stateKey = (lang: Lang) => `workbrawl:guess-day:${lang}`;
const statsKey = (lang: Lang) => `workbrawl:guess-stats:${lang}`;
const TOAST_MS = 2000;

export function renderDaily(root: HTMLElement): void {
  new DailyScreen(root);
}

class DailyScreen {
  private lang: Lang = getWordLang();
  private state: DayState | null = null;
  private readonly draft = new Draft();
  private busy = false;
  private readonly grid = new GuessGrid((column) => this.select(column));
  private keyboard: Keyboard;
  private readonly keyboardSlot = h("div", { class: "keyboard-slot" });
  private readonly headingEl = h("h1", { class: "daily-heading" });
  private readonly messageEl = h("p", { class: "stage-status", "aria-live": "polite" });
  private readonly resultEl = h("section", { class: "card daily-result", hidden: true });
  private readonly toastEl = h("div", { class: "toast", role: "status", "aria-live": "polite", hidden: true });
  private toastTimer: ReturnType<typeof setTimeout> | undefined;
  private countdownTimer: ReturnType<typeof setInterval> | undefined;

  constructor(private readonly root: HTMLElement) {
    this.keyboard = this.makeKeyboard();
    const langSelect = wordLangSelect("day-lang");
    langSelect.addEventListener("change", () => {
      this.lang = langSelect.value as Lang;
      setWordLang(this.lang);
      this.keyboard = this.makeKeyboard();
      void this.load();
    });

    root.replaceChildren(
      h(
        "header",
        { class: "topbar" },
        h("a", { href: `${BASE_PATH}/`, class: "brand" }, brandMark()),
        h("div", { class: "room-info" }, uiLangSelect(true)),
      ),
      this.toastEl,
      h(
        "main",
        { class: "daily" },
        this.headingEl,
        h(
          "div",
          { class: "daily-lang" },
          h("label", { for: "day-lang" }, STRINGS.wordLanguageLabel),
          langSelect,
        ),
        h("div", { class: "card stage" }, this.grid.el, this.messageEl, this.keyboardSlot, contrastToggle()),
        this.resultEl,
      ),
      siteFooter(),
    );
    void this.load();
  }

  private makeKeyboard(): Keyboard {
    const keyboard = new Keyboard(this.lang, (key) => this.onKey(key));
    this.keyboardSlot.replaceChildren(keyboard.el);
    return keyboard;
  }

  /** Today's number, plus whatever this browser already tried today. */
  private async load(): Promise<void> {
    this.draft.clear();
    let today: DailyToday;
    try {
      const res = await fetch(`${BASE_PATH}/api/daily/today`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      today = (await res.json()) as DailyToday;
    } catch {
      this.toast(STRINGS.networkError);
      return;
    }
    const saved = read<DayState>(stateKey(this.lang));
    this.state =
      saved && saved.day === today.day
        ? saved
        : { day: today.day, number: today.number, guesses: [], marks: [], done: false, won: false, answer: null };
    this.render();
  }

  private onKey(key: string): void {
    const state = this.state;
    if (!state || state.done || this.busy) return;
    if (key === "backspace") this.draft.backspace();
    else if (key === "left") this.draft.move(-1);
    else if (key === "right") this.draft.move(1);
    else if (key === "enter") {
      void this.submit();
      return;
    } else this.draft.type(key);
    this.render();
  }

  /** A click on a tile of the row being typed: the next letter goes there. */
  private select(column: number): void {
    const state = this.state;
    if (!state || state.done || this.busy) return;
    this.draft.select(column);
    this.render();
  }

  private async submit(): Promise<void> {
    const state = this.state!;
    if (!this.draft.complete) {
      this.toast(STRINGS.tooShort);
      this.grid.shake(state.guesses.length);
      return;
    }
    this.busy = true;
    try {
      const word = this.draft.word;
      const res = await postJson(`${BASE_PATH}/api/daily/guess`, { lang: this.lang, guess: word });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as DailyGuessResponse;
      if ("error" in body) {
        this.toast(body.error === "NOT_A_WORD" ? STRINGS.notAWord : STRINGS.tooShort);
        this.grid.shake(state.guesses.length);
        return;
      }
      if (body.day !== state.day) {
        // Midnight passed while the page was open: start the new day.
        await this.load();
        return;
      }
      state.guesses.push(word);
      state.marks.push(body.marks);
      this.draft.clear();
      if (body.marks.every((m) => m === 2)) {
        state.done = true;
        state.won = true;
        state.answer = state.guesses.at(-1)!;
      } else if (state.guesses.length >= MAX_ATTEMPTS) {
        state.done = true;
        state.answer = await this.reveal(state);
      }
      write(stateKey(this.lang), state);
      if (state.done) this.countDay(state);
      this.render(true);
    } catch {
      this.toast(STRINGS.networkError);
    } finally {
      this.busy = false;
    }
  }

  private async reveal(state: DayState): Promise<string | null> {
    try {
      const res = await postJson(`${BASE_PATH}/api/daily/answer`, { lang: this.lang, guesses: state.guesses });
      if (!res.ok) return null;
      return ((await res.json()) as DailyAnswerResponse).answer;
    } catch {
      return null;
    }
  }

  /** Statistics, counted once per day and language. */
  private countDay(state: DayState): void {
    const stats = readStats(this.lang);
    if (stats.lastDay === state.day) return;
    const yesterday = new Date(Date.parse(`${state.day}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
    stats.played++;
    if (state.won) {
      stats.won++;
      stats.streak = stats.lastDay === yesterday && stats.streak > 0 ? stats.streak + 1 : 1;
      stats.best = Math.max(stats.best, stats.streak);
      stats.distribution[state.guesses.length - 1]!++;
    } else {
      stats.streak = 0;
    }
    stats.lastDay = state.day;
    write(statsKey(this.lang), stats);
    track("round_finished", { mode: "daily", word_language: this.lang, solved: state.won ? 1 : 0, tries: state.guesses.length });
  }

  private render(animate = false): void {
    const state = this.state;
    if (!state) return;
    this.headingEl.textContent = STRINGS.dayHeading(state.number);
    this.grid.render(state.guesses, state.marks, state.done ? null : this.draft, animate);
    this.keyboard.color(state.guesses, state.marks);
    this.keyboard.setEnabled(!state.done);
    this.messageEl.textContent = "";
    this.renderResult();
  }

  private renderResult(): void {
    const state = this.state!;
    clearInterval(this.countdownTimer);
    if (!state.done) {
      this.resultEl.hidden = true;
      return;
    }
    const share = h("button", { type: "button", class: "btn btn-primary" }, STRINGS.shareResult);
    share.addEventListener("click", () => void this.share());
    const next = h("strong", { class: "daily-next" });
    const tick = () => (next.textContent = hms(msUntilNextDay()));
    tick();
    this.countdownTimer = setInterval(tick, 1000);

    const stats = readStats(this.lang);
    const max = Math.max(1, ...stats.distribution);
    const answer = state.answer ? [h("p", { class: "results-headline" }, STRINGS.answerWas(state.answer.toUpperCase()))] : [];
    this.resultEl.replaceChildren(
      h("h2", {}, state.won ? STRINGS.dayWon : STRINGS.dayLost),
      ...answer,
      share,
      h("p", { class: "muted" }, `${STRINGS.nextWord} `, next),
      h("h3", { class: "panel-title" }, STRINGS.stats),
      h(
        "dl",
        { class: "daily-stats" },
        ...[
          [STRINGS.statPlayed, stats.played],
          [STRINGS.statWinRate, stats.played ? Math.round((stats.won / stats.played) * 100) : 0],
          [STRINGS.statStreak, stats.streak],
          [STRINGS.statBest, stats.best],
        ].flatMap(([label, value]) => [h("div", {}, h("dd", {}, String(value)), h("dt", {}, String(label)))]),
      ),
      h("h3", { class: "panel-title" }, STRINGS.statDistribution),
      h(
        "ol",
        { class: "daily-distribution" },
        ...stats.distribution.map((count, i) => {
          const bar = h("span", { class: "bar" }, String(count));
          bar.style.width = `${Math.max(8, (count / max) * 100)}%`;
          if (state.won && state.guesses.length === i + 1) bar.classList.add("is-today");
          return h("li", {}, h("span", { class: "muted" }, String(i + 1)), bar);
        }),
      ),
    );
    this.resultEl.hidden = false;
  }

  private async share(): Promise<void> {
    const state = this.state!;
    const score = state.won ? `${state.guesses.length}/${MAX_ATTEMPTS}` : `X/${MAX_ATTEMPTS}`;
    // UTM tags let Analytics count the visits that shared results bring.
    const link = `https://workbrawl.com${BASE_PATH}/day?utm_source=share&utm_medium=daily_result&utm_campaign=guessbrawl`;
    const text = `GuessBrawl #${state.number} ${score}\n\n${shareSquares(state.marks)}\n\n${link}`;
    try {
      if (typeof navigator.share === "function" && matchMedia("(pointer: coarse)").matches) {
        await navigator.share({ text });
        track("result_shared", { method: "native", title: "guessbrawl_daily" });
        return;
      }
      await navigator.clipboard.writeText(text);
      track("result_shared", { method: "copy_text", title: "guessbrawl_daily" });
      this.toast(STRINGS.resultCopied);
    } catch {
      // Share sheet dismissed, or clipboard blocked: nothing else to do.
    }
  }

  private toast(text: string): void {
    clearTimeout(this.toastTimer);
    this.toastEl.textContent = text;
    this.toastEl.hidden = false;
    this.toastTimer = setTimeout(() => (this.toastEl.hidden = true), TOAST_MS);
  }
}

/** h:mm:ss, for the countdown to the next word. */
function hms(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${Math.floor(total / 3600)}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
}

/** Milliseconds until midnight in the shared time zone. */
function msUntilNextDay(): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: DAILY_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const value = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const elapsed = (value("hour") * 3600 + value("minute") * 60 + value("second")) * 1000;
  return 86_400_000 - elapsed;
}

function readStats(lang: Lang): Stats {
  return (
    read<Stats>(statsKey(lang)) ?? { played: 0, won: 0, streak: 0, best: 0, lastDay: null, distribution: Array(MAX_ATTEMPTS).fill(0) }
  );
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable: today's attempts just won't survive a reload.
  }
}

function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
}
